import {createAsyncThunk} from '@reduxjs/toolkit';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {RootState} from 'common/src/redux/store';
import aiContentItemApi from '../../../aiContentItem.api';
import {GenerateAssetsRequest} from '../../../aiContentItem.types';
import {backgroundProcessService} from 'common/src/modules/backgroundProcess/backgroundProcess.service';
import {ProcessType} from 'common/src/modules/backgroundProcess/backgroundProcess.types';
import {jobsApi} from 'common/src/modules/jobs/jobs.api';
import {JobUIStatus} from 'common/src/modules/jobs/jobs.types';
import loadAiContentItems from './loadAiContentItems';

const JOB_POLL_INTERVAL_MS = 5000;

type GenerateAssetsParams = GenerateAssetsRequest & {
  projectId: number;
};

const generateAssets = createAsyncThunk<number, GenerateAssetsParams, {state: RootState}>(
  `${BreadcrumbName.AiContentProjectPage}/generateAssets`,
  async ({projectId, itemIds, adSizes, deliverableKeys}, {dispatch, rejectWithValue}) => {
    const processId = `generate-assets-${projectId}-${Date.now()}`;
    const bgProcess = backgroundProcessService.startProcess(
      `Generate Assets (${itemIds.length} item${itemIds.length > 1 ? 's' : ''})`,
      processId,
      ProcessType.REMOTE,
    );

    try {
      const jobId = await aiContentItemApi.generateAssets(projectId, {
        itemIds,
        adSizes,
        deliverableKeys,
      });

      bgProcess.setJobId(jobId);

      // Refresh items to show "GeneratingAssets" status
      dispatch(loadAiContentItems({projectId, forceReload: true}));

      // Start polling for job completion (fire-and-forget)
      const pollInterval = setInterval(async () => {
        try {
          const jobStatus = await jobsApi.getJobExecution(jobId);

          if (jobStatus.status === JobUIStatus.Completed) {
            clearInterval(pollInterval);
            bgProcess.success();
            dispatch(loadAiContentItems({projectId, forceReload: true}));
          } else if (
            jobStatus.status === JobUIStatus.Failed ||
            jobStatus.status === JobUIStatus.Aborted
          ) {
            clearInterval(pollInterval);
            bgProcess.fail(new Error(jobStatus.message || 'Job failed'));
            dispatch(loadAiContentItems({projectId, forceReload: true}));
          }
        } catch (_pollError) {
          clearInterval(pollInterval);
          bgProcess.fail(new Error('Failed to check job status'));
        }
      }, JOB_POLL_INTERVAL_MS);

      return jobId;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate assets';
      bgProcess.fail(new Error(errorMessage));

      return rejectWithValue(errorMessage);
    }
  },
);

export default generateAssets;
